import { useState, useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { MARC_PUBKEY, type ArtPhoto } from '@/hooks/useArtstrPhotos';
import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { ImagePlus, Loader2, X } from 'lucide-react';

const IMAGE_EXT_REGEX = /\.(jpg|jpeg|png|gif|webp|avif|apng)(?:[?#]\S*)?$/i;

/** Validate + normalize a pasted photo URL. Returns null if invalid. */
function validatePhotoUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  // https only (defense-in-depth: this becomes an <img src>)
  if (url.protocol !== 'https:') return null;
  return url.toString();
}

interface AddPhotoFieldProps {
  /** Called with an optimistic photo so it appears immediately in the grid. */
  onAdded?: (photo: ArtPhoto) => void;
  /** Gallery relays to also publish to, so the photo is queryable here. */
  relays?: string[];
}

export function AddPhotoField({ onAdded, relays = [] }: AddPhotoFieldProps) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Only Marc can add photos.
  const isOwner = user?.pubkey === MARC_PUBKEY;

  const handleSubmit = useCallback(async () => {
    const validUrl = validatePhotoUrl(url);
    if (!validUrl) {
      toast({
        title: 'Invalid URL',
        description: 'Please paste a valid https:// image URL.',
        variant: 'destructive',
      });
      return;
    }
    if (!user) return;

    setSubmitting(true);
    try {
      const looksLikeImage = IMAGE_EXT_REGEX.test(validUrl);
      const createdAt = Math.floor(Date.now() / 1000);

      // Kind 20 (NIP-68 picture event) with #artstr tag + imeta so it flows
      // through the existing gallery pipeline.
      const tags: string[][] = [
        ['t', 'artstr'],
        ['imeta', `url ${validUrl}`, ...(looksLikeImage ? ['m image/jpeg'] : [])],
        ['alt', caption.trim() || "Artwork added to Marc's Nostr Art Gallery"],
      ];

      const unsigned = {
        kind: 20,
        content: caption.trim(),
        tags,
        created_at: createdAt,
      };

      const signed = await user.signer.signEvent(unsigned);

      // Publish to the default pool AND directly to the gallery relays so the
      // photo is immediately discoverable by this site's queries.
      const publishTasks = [
        nostr.event(signed, { signal: AbortSignal.timeout(5000) }),
      ];
      if (relays.length > 0) {
        publishTasks.push(
          nostr.group(relays).event(signed, { signal: AbortSignal.timeout(5000) }),
        );
      }
      await Promise.allSettled(publishTasks);

      // Optimistically show it right away.
      onAdded?.({
        event: signed,
        url: validUrl,
        alt: caption.trim(),
        caption: caption.trim(),
      });

      toast({
        title: 'Photo added!',
        description: 'Your artwork has been published to Nostr and added to the gallery.',
      });

      setUrl('');
      setCaption('');
      setOpen(false);
    } catch (err) {
      toast({
        title: 'Could not add photo',
        description: err instanceof Error ? err.message : 'Something went wrong publishing to Nostr.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  }, [url, caption, user, nostr, toast, onAdded]);

  // Not the owner → render nothing (keeps the gallery clean for visitors).
  if (!isOwner) {
    // If nobody is logged in, offer a subtle login entry so Marc can sign in.
    if (!user) {
      return (
        <div className="mb-8 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-primary/30 px-6 py-4">
            <p className="text-xs text-muted-foreground">Gallery owner? Sign in to add photos.</p>
            <LoginArea className="max-w-60" />
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mb-8">
      {!open ? (
        <div className="flex justify-center">
          <Button
            onClick={() => setOpen(true)}
            className="gap-2 rounded-xl"
          >
            <ImagePlus className="size-4" />
            Add a photo by URL
          </Button>
        </div>
      ) : (
        <div className="mx-auto max-w-2xl rounded-xl border border-primary/40 bg-card p-4 sm:p-6 shadow-lg">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Add a photo</h3>
            <button
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Close"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <label htmlFor="photo-url" className="text-sm font-medium text-foreground">
                Photo URL
              </label>
              <Input
                id="photo-url"
                type="url"
                inputMode="url"
                placeholder="https://blossom.primal.net/…​.jpg"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                autoFocus
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                Paste a direct <span className="font-mono">https://</span> link to an image.
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="photo-caption" className="text-sm font-medium text-foreground">
                Caption <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <Textarea
                id="photo-caption"
                placeholder="Describe your artwork…"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={2}
                disabled={submitting}
                className="resize-none"
              />
            </div>

            {/* Live preview of the pasted URL */}
            {validatePhotoUrl(url) && (
              <div className="rounded-lg overflow-hidden border border-primary/20 bg-black/40">
                <img
                  src={validatePhotoUrl(url)!}
                  alt="Preview"
                  className="w-full max-h-64 object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !validatePhotoUrl(url)}
                className="gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Publishing…
                  </>
                ) : (
                  <>
                    <ImagePlus className="size-4" />
                    Add to Gallery
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
